import { useState, useRef, useEffect } from "react";
import { HelpCircle, ChevronRight, X } from "lucide-react";
import { useHelp } from "../../lib/help";
import { HELP_TOPICS } from "../../constants/helpContent";

export default function HelpTip({ text, topic, className = "" }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const { openHelp } = useHelp();

  // Resolve content: if text is provided use it, otherwise fall back to helpContent.js
  const topicData = topic ? HELP_TOPICS[topic] : null;
  const contentText = text || topicData?.short || "Click Learn More for a comprehensive landlord guide.";

  // Calculate clean popover position relative to trigger
  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = Math.min(320, window.innerWidth - 32);
    
    // Position below or above depending on viewport space
    let top = rect.bottom + window.scrollY + 8;
    let left = rect.left + window.scrollX - popoverWidth / 2 + rect.width / 2;

    // Constrain within viewport bounds
    if (left < 16) left = 16;
    if (left + popoverWidth > window.innerWidth - 16) {
      left = window.innerWidth - popoverWidth - 16;
    }

    setCoords({ top, left });
  };

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open) {
      updatePosition();
    }
    setOpen((prev) => !prev);
  };

  // Close on click outside or Escape
  useEffect(() => {
    if (!open) return;

    const handleDown = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    const handleScroll = () => {
      if (open) updatePosition();
    };

    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  const handleLearnMore = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    if (topic) {
      openHelp(topic);
    }
  };

  return (
    <span className={`inline-flex items-center align-middle ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-label={topicData?.title ? `Help for ${topicData.title}` : "Help tooltip"}
        className="w-4 h-4 rounded-full flex items-center justify-center text-[#6B7280] hover:text-teal-400 hover:bg-teal-500/10 transition-colors cursor-pointer outline-none focus-visible:ring-1 focus-visible:ring-teal-400"
        data-testid={`helptip-btn-${topic || "generic"}`}
      >
        <HelpCircle size={13} className="shrink-0" />
      </button>

      {open && (
        <div
          ref={popoverRef}
          style={{
            position: "absolute",
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: "max-content",
            maxWidth: "min(320px, calc(100vw - 32px))",
            zIndex: 999,
          }}
          className="p-3.5 rounded-2xl bg-[#13131A] text-slate-100 text-xs border border-[#1E1E2E] shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150"
          data-testid={`helptip-popover-${topic || "generic"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <span className="font-bold text-teal-400 text-xs tracking-wide">
              {topicData?.title || "Landlord Guide"}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[#6B7280] hover:text-white p-0.5"
            >
              <X size={13} />
            </button>
          </div>

          <p className="text-[11.5px] leading-relaxed text-slate-300">
            {contentText}
          </p>

          {topic && (
            <div className="mt-2.5 pt-2 border-t border-[#1E1E2E] flex justify-end">
              <button
                type="button"
                onClick={handleLearnMore}
                className="text-[11px] font-semibold text-teal-400 hover:text-teal-300 flex items-center gap-0.5 group"
                data-testid={`helptip-learnmore-${topic}`}
              >
                <span>Learn more</span>
                <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          )}
        </div>
      )}
    </span>
  );
}
